import {
  Check,
  ChevronDown,
  CircleCheck,
  Copy,
  Download,
  FolderGit2,
  History,
  House,
  Inbox,
  Monitor,
  Moon,
  PanelLeft,
  Plus,
  Search,
  Settings,
  Sun,
  TriangleAlert,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { explain, isDenied, isNotFound, isSignedOut } from "./api/client";
import { Link } from "@tanstack/react-router";
import { useConnection } from "./connection";

const icons = {
  changes: History,
  search: Search,
  down: ChevronDown,
  copy: Copy,
  check: Check,
  sun: Sun,
  moon: Moon,
  monitor: Monitor,
  home: House,
  inbox: Inbox,
  settings: Settings,
  repo: FolderGit2,
  users: Users,
  ok: CircleCheck,
  warn: TriangleAlert,
  x: X,
  menu: PanelLeft,
  plus: Plus,
  download: Download,
} as const;
export type IconName = keyof typeof icons;

/** An icon by name, or (in views loaded later, so the first screen stays small) a lucide icon itself. */
export function Icon({ name, size = 16, label }: { name: IconName | LucideIcon; size?: number; label?: string }) {
  const C = typeof name === "string" ? icons[name] : name;
  return <C size={size} strokeWidth={1.75} className="icon" role={label ? "img" : undefined} aria-label={label} aria-hidden={label ? undefined : true} />;
}

// ---- time ----

let now = Date.now();
const subscribers = new Set<() => void>();
setInterval(() => {
  now = Date.now();
  subscribers.forEach((s) => s());
}, 15_000);

/** The current time, updated every 15 seconds for every component that shows an age. */
export function useNow() {
  return useSyncExternalStore(
    (s) => (subscribers.add(s), () => subscribers.delete(s)),
    () => now,
  );
}

export function ago(ms: number, at: number): string {
  if (ms <= 0) return "never";
  const s = Math.max(0, Math.round((at - ms) / 1000));
  if (s < 45) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(ms).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

const full = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "medium" });

export function Time({ ms }: { ms: number }) {
  const at = useNow();
  if (!ms) return <span className="muted">no time</span>;
  return (
    <time dateTime={new Date(ms).toISOString()} title={full.format(ms)} className="num">
      {ago(ms, Math.max(at, Date.now() - 15_000))}
    </time>
  );
}

// ---- people and agents ----

function hash(s: string) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** Agents are named `agent/<role>-<n>` by convention; they get square marks, people round. */
export function parseAuthor(author: string) {
  // Imported git history names authors "Name <email>": show the name; the address stays in the tooltip.
  author = author.replace(/\s*<[^>]*>$/, "") || author;
  const agent = /^(agent|bot)s?\//i.test(author) || /\[bot\]$/.test(author);
  const slash = author.lastIndexOf("/");
  const ns = slash >= 0 ? author.slice(0, slash + 1) : "";
  const name = author.slice(slash + 1) || author || "unknown";
  const words = name.split(/[-_.\s@]+/).filter(Boolean);
  const digits = name.match(/\d+$/)?.[0];
  const initials =
    digits && words[0]
      ? words[0][0]! + String(Number(digits)).slice(-1)
      : words
          .slice(0, 2)
          .map((w) => w[0])
          .join("");
  return { agent, ns, name, initials: initials.toUpperCase().slice(0, 2), color: `var(--who-${hash(author) % 8})` };
}

/** Someone's mark and name; with `link`, a person's name opens their page (agents and names
 * from imported history have none). Not inside another link. */
export function Author({ author, compact, large, link }: { author: string; compact?: boolean; large?: boolean; link?: boolean }) {
  const a = parseAuthor(author);
  if (link && !compact && !a.agent && /^[A-Za-z0-9][\w.-]*$/.test(author))
    return (
      <Link to="/$owner" params={{ owner: author }} className="who who-link" title={`${author}: their page`}>
        <span className="who-mark" data-size={large ? "lg" : undefined} style={{ "--c": a.color } as React.CSSProperties} aria-hidden="true">
          {a.initials}
        </span>
        <span className="who-name">{a.name}</span>
      </Link>
    );
  return (
    <span className="who" title={a.agent ? `${author} (agent)` : author}>
      <span
        className="who-mark"
        data-agent={a.agent || undefined}
        data-size={large ? "lg" : undefined}
        style={{ "--c": a.color } as React.CSSProperties}
        aria-hidden="true"
      >
        {a.initials}
      </span>
      {!compact && (
        <span className="who-name">
          {a.ns && <span className="ns">{a.ns}</span>}
          {a.name}
        </span>
      )}
      {compact && <span className="sr-only">{author}</span>}
    </span>
  );
}

// ---- ids ----

export function CopyId({ id, label = "Copy id" }: { id: string; label?: string }) {
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => setDone(false), 1400);
    return () => clearTimeout(t);
  }, [done]);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setDone(true);
    } catch {
      window.getSelection()?.selectAllChildren(document.getElementById(`id-${id}`)!);
    }
  };
  return (
    <button type="button" className="copy" onClick={copy} aria-label={`${label} ${id}`} title={id}>
      <span id={`id-${id}`}>{id.slice(0, 12)}</span>
      <Icon name={done ? "check" : "copy"} size={12} />
      <span className="sr-only" aria-live="polite">
        {done ? "Copied" : ""}
      </span>
    </button>
  );
}

// ---- states ----

export function Skeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="skeleton" role="status" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <i key={i} style={{ width: `${55 + ((i * 37) % 40)}%` }} />
      ))}
    </div>
  );
}

export function ErrorState({ error, retry, title = "This didn't load" }: { error: unknown; retry?: () => void; title?: string }) {
  const { target, signOut } = useConnection();
  return (
    <div className="state" role="alert">
      <h2>{title}</h2>
      <p>
        <Words text={explain(error, target.url)} />
      </p>
      {isSignedOut(error) ? (
        <button type="button" className="button" data-primary="" onClick={() => void signOut()}>
          Sign in again
        </button>
      ) : (
        retry && (
          <button type="button" className="button" onClick={retry}>
            Try again
          </button>
        )
      )}
    </div>
  );
}

/** A page for something that isn't on this server: say what was looked for and where to go. */
export function NotFound({ what, error }: { what: string; error?: unknown }) {
  // The server says where a renamed repository went, to those who may read it there.
  const moved = error ? /renamed to `([^/`]+)\/([^`]+)`/.exec(explain(error)) : null;
  if (moved)
    return (
      <div className="state">
        <h2>This repository was renamed</h2>
        <p>
          It's now <span className="mono">{`${moved[1]}/${moved[2]}`}</span>. Update any git remotes or agents that use the old name.
        </p>
        <p>
          <Link to="/$owner/$repo" params={{ owner: moved[1]!, repo: moved[2]! }} className="button" data-primary="">
            Go to {moved[1]}/{moved[2]}
          </Link>
        </p>
      </div>
    );
  return (
    <div className="state">
      <h2>There's no {what} here</h2>
      <p>It may have been renamed or deleted, or it's private: check the address, or ask whoever sent it whether you have access.</p>
      <p>
        <Link to="/" className="button">
          Go to Home
        </Link>
      </p>
    </div>
  );
}

/** An error, or a "not found" page when the error says so. */
export function LoadError({ error, what, title, retry }: { error: unknown; what: string; title: string; retry?(): void }) {
  if (isNotFound(error)) return <NotFound what={what} error={error} />;
  if (isDenied(error)) return <NoAccess what={`this ${what}`} />;
  return <ErrorState error={error} title={title} retry={retry} />;
}

/** A page for something this account may not see or change: say so, and who can help. */
export function NoAccess({ what, children }: { what: string; children?: ReactNode }) {
  return (
    <div className="state">
      <h2>You don't have access to {what}</h2>
      <p>{children ?? "Ask an administrator of this organisation to give you access."}</p>
    </div>
  );
}

/** Server messages quote names in backticks (`main`); show those as code. */
export function Words({ text }: { text: string }) {
  // One element, so a message stays one block of text wherever it's placed (a flex row
  // would otherwise make each piece a column).
  return <span className="words">{text.split(/`([^`]+)`/).map((part, i) => (i % 2 ? <code key={i}>{part}</code> : part))}</span>;
}

// ---- feedback: toasts and confirmations, shown by the shell for every view ----

/** A tiny store any component can subscribe to. */
function store<T>(initial: T) {
  let value = initial;
  const subs = new Set<() => void>();
  return {
    get: () => value,
    set(v: T) {
      value = v;
      subs.forEach((s) => s());
    },
    useValue: () =>
      useSyncExternalStore(
        (s) => (subs.add(s), () => subs.delete(s)),
        () => value,
      ),
  };
}

type Toast = { id: number; text: string; kind: "ok" | "error" };
const toasts = store<Toast[]>([]);
let toastId = 0;

/** Says what an action did ("Member removed") or why it failed, to sighted and screen-reader users. */
export function toast(text: string, kind: Toast["kind"] = "ok") {
  const t = { id: ++toastId, text, kind };
  toasts.set([...toasts.get(), t]);
  setTimeout(() => toasts.set(toasts.get().filter((x) => x !== t)), kind === "error" ? 8000 : 3500);
}

/** Toasts an action's failure in words a person can act on. */
export const toastError = (e: unknown) => toast(explain(e), "error");

export function Toaster() {
  const list = toasts.useValue();
  return (
    <div className="toasts" aria-live="polite">
      {list.map((t) => (
        <div key={t.id} className="toast" data-kind={t.kind} role={t.kind === "error" ? "alert" : "status"}>
          <Icon name={t.kind === "error" ? "warn" : "ok"} size={15} />
          <span>
            <Words text={t.text} />
          </span>
          <button type="button" className="icon-button" aria-label="Dismiss" onClick={() => toasts.set(toasts.get().filter((x) => x !== t))}>
            <Icon name="x" size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}

type Ask = { title: string; body?: ReactNode; action: string; typed?: string; resolve(ok: boolean): void };
const asking = store<Ask | null>(null);

/**
 * Asks before something that can't be undone. `typed` makes the person type a name first,
 * for the few actions that destroy data.
 */
export function confirmAction(o: Omit<Ask, "resolve">): Promise<boolean> {
  asking.get()?.resolve(false);
  return new Promise((resolve) => asking.set({ ...o, resolve }));
}

export function ConfirmHost() {
  const ask = asking.useValue();
  const dialog = useRef<HTMLDialogElement>(null);
  const [typed, setTyped] = useState("");
  useEffect(() => {
    setTyped("");
    if (ask) dialog.current?.showModal();
    else dialog.current?.close();
  }, [ask]);
  const done = (ok: boolean) => {
    ask?.resolve(ok);
    asking.set(null);
  };
  return (
    <dialog ref={dialog} className="confirm" aria-labelledby="confirm-title" onCancel={() => done(false)}>
      {ask && (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            done(true);
          }}
        >
          <h2 id="confirm-title">{ask.title}</h2>
          {ask.body && <p>{ask.body}</p>}
          {ask.typed && (
            <label className="field">
              <span>
                Type <b>{ask.typed}</b> to confirm
              </span>
              <input className="input" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false} autoFocus />
            </label>
          )}
          <div className="confirm-actions">
            <button type="button" className="button" onClick={() => done(false)} autoFocus={!ask.typed}>
              Cancel
            </button>
            <button type="submit" className="button" data-danger="" disabled={!!ask.typed && typed !== ask.typed}>
              {ask.action}
            </button>
          </div>
        </form>
      )}
    </dialog>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="state">
      <h2>{title}</h2>
      {children && <p>{children}</p>}
    </div>
  );
}

export function formatBytes(n: number) {
  // One decimal, dropped when it's zero: "1 GB", "1.5 GB".
  const one = (v: number) => v.toFixed(1).replace(/\.0$/, "");
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${one(n / 1024)} KB`;
  if (n < 1024 ** 3) return `${one(n / 1024 ** 2)} MB`;
  return `${one(n / 1024 ** 3)} GB`;
}

/** Text if the bytes look like text (no NUL in the first 8 KB and valid UTF-8), else null. */
export function asText(bytes: Uint8Array): string | null {
  if (bytes.subarray(0, 8192).includes(0)) return null;
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return null;
  }
}
